import { useRef } from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { getUserFacingErrorMessage } from '@/services/api/errors';
import { useUpdateSqlImportQuery, type SqlImportQueryWriteInput } from '@/services/db/sql-import/mutations';
import type { SqlImportDraft } from '../../new/draft';

const SAVE_FAILED_MESSAGE = "The query couldn't be saved.";

type UseSaveQueryArgs = {
  workspaceId: number;
  sqlImportId: string;
  /** The save landed; the tab re-baselines on the draft that was sent. */
  onSaved: (sent: SqlImportDraft) => void;
};

/** The `update_query` write behind the Query and Relationship Query tabs, with Ember's snackbars. */
export function useSaveQuery({ workspaceId, sqlImportId, onSaved }: UseSaveQueryArgs) {
  const snackbars = useSnackbars();
  const update = useUpdateSqlImportQuery({ workspaceId });
  // Two clicks before the next render must not become two requests; the pending flag lands a
  // render late, so the latch is a ref.
  const inFlight = useRef(false);

  const save = async (sent: SqlImportDraft, body: SqlImportQueryWriteInput): Promise<boolean> => {
    if (inFlight.current) {
      return false;
    }
    inFlight.current = true;
    try {
      await update.mutateAsync({ sqlImportId, ...body });
      onSaved(sent);
      snackbars.create({ id: 'sqlImports.query.updated', message: 'Query updated' });
      return true;
    } catch (error) {
      snackbars.create({
        id: 'sqlImports.query.error',
        variant: 'critical',
        message: getUserFacingErrorMessage(error, SAVE_FAILED_MESSAGE),
      });
      return false;
    } finally {
      inFlight.current = false;
    }
  };

  return { save, isSaving: update.isPending };
}
